// Copyright 2025, University of Maryland, All Rights Reserved
import { useEffect } from 'react';
import { Settings } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { configApi } from '../api/config';
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component';
import type { ConfigurationLevel, TemplateResponse } from '../types/config';

interface TemplatesListProps {
  level: ConfigurationLevel;
  selectedTemplate: TemplateResponse | null;
  onSelectTemplate: (template: string) => void;
}

export function TemplatesList({
  level,
  selectedTemplate,
  onSelectTemplate,
}: TemplatesListProps) {
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth();

  const { data: templates, isLoading } = useQuery({
    queryKey: level === 'default'
      ? ['templates', level]
      : ['templates', level, selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => configApi.getTemplates(
      level,
      level === 'workspace' ? selectedOrganizationId || undefined : undefined,
      level === 'workspace' ? selectedWorkspaceId || undefined : undefined
    ),
    enabled: level === 'default' || Boolean(selectedOrganizationId && selectedWorkspaceId),
  });

  // Auto-select the first template only when templates first load
  useEffect(() => {
    if (templates && templates.length > 0 && !selectedTemplate) {
      onSelectTemplate(templates[0]);
    }
  }, [templates?.length]); // Only depend on templates length changing

  if (isLoading) {
    return (
      <div className="lg:col-span-1 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg text-gray-700">📋 Templates</h3>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
          <div className="text-center py-8 text-gray-500">Loading templates...</div>
        </div>
      </div>
    );
  }
  return (
    <div className="lg:col-span-1 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg text-gray-700">📋 Templates</h3>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
        <div className="space-y-2">
          {templates?.map((template) => {
            const isSelected = selectedTemplate?.name === template;
            return (
              <button
                key={template}
                onClick={() => onSelectTemplate(template)}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors border ${
                  isSelected
                    ? 'bg-blue-100 text-blue-800 border-blue-300'
                    : 'bg-white hover:bg-gray-50 border-gray-200'
                }`}
              >
                <div className="font-medium">{template}</div>
              </button>
            );
          })}
          {(!templates || templates.length === 0) && (
            <div className="text-center py-8 text-gray-500">
              <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No templates found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
